/**
 * gift-promo-selector.js
 * ----------------------------------------------------------------------------
 * Custom element that powers the "Buy X get 1 gift" promo selector inside
 * the cart drawer and on the cart page.
 *
 * The Liquid (`snippets/gift-promo-selector.liquid`) is responsible for
 * deciding whether the promo is currently active (it has access to
 * `cart.discount_applications` and `item.product.collections`). The element
 * always renders into the cart, so on every cart update it is recreated with
 * up-to-date data attributes.
 *
 * Activation rule (Liquid):
 *   gift_promo_active = qualifying_count >= min_quantity
 *   (the discount code is NOT required for the selector to appear).
 *
 * On gift selection the JS:
 *   1. Removes any existing gift line in the cart (so we never duplicate).
 *   2. Adds the chosen variant via /cart/add.js with the line item properties
 *      `_promo_gift` + `_is_free_gift`.
 *   3. Auto-applies the configured discount code via /cart/update.js, marking
 *      the cart with `attributes[gift_promo_auto_applied] = "true"` so we
 *      know later that we own that code (a customer-typed code is left
 *      alone). Now the cart has 2 buys + 1 gift = 3 polishes, which makes
 *      the "Buy 2 get 1" discount valid and Shopify allocates the free €0
 *      slot to one of the polishes.
 *   4. Dispatches `cart:refresh` so the cart drawer / cart page re-renders
 *      with the latest server-side state (including the €-12 discount line).
 *
 * Cleanup happens automatically on every cart re-render (because Liquid
 * recreates this element with fresh data attributes):
 *   - If the promo is no longer active and a gift line still exists →
 *     remove the gift line.
 *   - If we had auto-applied the code and there's no gift line in cart any
 *     more → remove the code so the cart is clean (we only ever remove a
 *     code we own; manually-typed ones are left alone).
 */

class GiftPromoSelector extends HTMLElement {
  constructor() {
    super();
    this.isProcessing = false;
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.toggleSiblingRecommendations();

    if (this.isActive) {
      this.bindEvents();
    }

    // Cleanup runs after a microtask so the rest of the cart-drawer body has
    // finished mounting first; otherwise concurrent network calls can race.
    // It runs regardless of `isActive` because we need to detect cases where
    // the gift selector is hidden but the gift line / auto-applied code
    // need to be removed.
    queueMicrotask(() => this.cleanupIfNeeded());
  }

  /**
   * Hide / show the sibling recommendations element. Liquid in the parent
   * sections tries to skip rendering recommendations when the promo is
   * active, but this is a defensive belt-and-suspenders for any edge case
   * where both end up in the DOM together (e.g. on the cart page where we
   * always render both for hot-reload simplicity).
   */
  toggleSiblingRecommendations() {
    const parent = this.parentElement;
    if (!parent) return;
    parent.querySelectorAll('cart-drawer-products-recommendation').forEach((sibling) => {
      if (sibling === this) return;
      sibling.hidden = this.isActive;
    });

    // Cart page wraps recommendations in `#MainCartRecommendations-...`.
    const grandparent = parent.parentElement;
    if (grandparent) {
      grandparent.querySelectorAll('[id^="MainCartRecommendations-"]').forEach((sibling) => {
        if (sibling.contains(this)) return;
        sibling.hidden = this.isActive;
      });
    }
  }

  bindEvents() {
    // Only the "Добави подарък" button triggers a gift add. The variant
    // <select> is intentionally NOT wired to the cart — changing the
    // variant just updates the dropdown value, and the user has to press
    // the button explicitly to commit the choice.
    this.addEventListener('click', this.handleClick.bind(this));
  }

  /**
   * Click on the "Добави подарък" button → select that gift.
   * Clicks anywhere else on the card (image, title, variant <select>,
   * background) are intentionally ignored.
   */
  handleClick(event) {
    const trigger = event.target.closest('[data-gift-select]');
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();
    if (this.isProcessing) return;

    const card = trigger.closest('[data-gift-card]');
    if (!card) return;

    if (card.dataset.productAvailable === 'false') return;

    const inner = card.querySelector('[data-gift-card-inner]');
    const variantSelect = card.querySelector('[data-gift-variant-select]');
    const variantId = variantSelect ? variantSelect.value : inner?.dataset.initialVariantId;

    if (!variantId) return;

    this.selectGift(variantId, card);
  }

  /**
   * Add (or replace) the gift in the cart, then auto-apply the discount code.
   */
  async selectGift(variantId, card) {
    this.isProcessing = true;
    this.classList.add('is-loading');
    if (card) card.classList.add('is-loading');

    try {
      // Remove any existing gift line first (so we never duplicate).
      if (this.currentGiftLine) {
        await this.changeLineQuantity(this.currentGiftLine, 0);
      }

      // Add the chosen gift.
      const addResponse = await fetch(`${window.FoxTheme.routes.cart_add_url}`, {
        ...window.FoxTheme.utils.fetchConfig(),
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                _promo_gift: this.promoCode,
                _is_free_gift: 'true',
              },
            },
          ],
        }),
      });

      if (!addResponse.ok) {
        const error = await addResponse.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Failed to add gift');
      }

      // Now that the cart has enough items, apply the code (idempotent).
      await this.applyDiscountCode();

      this.dispatchCartRefresh();
    } catch (error) {
      console.error('[gift-promo-selector] Failed to select gift:', error);
    } finally {
      this.isProcessing = false;
      this.classList.remove('is-loading');
      if (card) card.classList.remove('is-loading');
    }
  }

  /**
   * Runs on every mount. Three cases we clean up after — note that
   * `isPromoEligible` (cart actually qualifies for the promo) is checked
   * separately from `isActive` (selector currently visible), because the
   * selector is hidden once the code is applied even though everything is
   * still valid.
   *
   *   1. Cart NOT eligible (qualifying items dropped below min) but a
   *      `_promo_gift` line is still in the cart → remove it, and if we
   *      auto-applied the code, remove that too.
   *   2. Cart eligible, gift in cart, but the auto-applied code is gone
   *      (user clicked the X to remove it) → remove the gift too so the
   *      customer doesn't get a "free" item that's no longer free.
   *   3. Cart eligible, no gift in cart, but the auto-applied code is
   *      still there → remove the orphan code so we surface a clean cart.
   */
  async cleanupIfNeeded() {
    if (this.isProcessing) return;

    // Case 1: cart no longer eligible.
    if (!this.isPromoEligible && this.currentGiftLine) {
      this.isProcessing = true;
      try {
        await this.changeLineQuantity(this.currentGiftLine, 0);
        if (this.autoApplied) {
          await this.removeAutoAppliedCode();
        }
        this.dispatchCartRefresh();
      } catch (error) {
        console.error('[gift-promo-selector] Failed to clean up orphan gift:', error);
      } finally {
        this.isProcessing = false;
      }
      return;
    }

    // Case 2: code we auto-applied was removed → also remove the gift.
    if (this.isPromoEligible && this.currentGiftLine && this.autoApplied && !this.codeApplied) {
      this.isProcessing = true;
      try {
        await this.changeLineQuantity(this.currentGiftLine, 0);
        await this.removeAutoAppliedCode();
        this.dispatchCartRefresh();
      } catch (error) {
        console.error('[gift-promo-selector] Failed to remove gift after code removal:', error);
      } finally {
        this.isProcessing = false;
      }
      return;
    }

    // Case 3: orphan code (no gift in cart but our marker still there).
    if (!this.currentGiftLine && this.autoApplied) {
      this.isProcessing = true;
      try {
        await this.removeAutoAppliedCode();
        this.dispatchCartRefresh();
      } catch (error) {
        console.error('[gift-promo-selector] Failed to remove orphan code:', error);
      } finally {
        this.isProcessing = false;
      }
    }
  }

  changeLineQuantity(line, quantity) {
    return fetch(`${window.FoxTheme.routes.cart_change_url}`, {
      ...window.FoxTheme.utils.fetchConfig(),
      body: JSON.stringify({
        line: Number(line),
        quantity: Number(quantity),
      }),
    }).then((response) => {
      if (!response.ok) throw new Error('Failed to update cart');
      return response.json();
    });
  }

  /**
   * Apply the promo discount code on top of any existing codes, marking the
   * cart so we know we own this code. Idempotent: if the code is already in
   * the cart we just refresh the marker attribute.
   */
  async applyDiscountCode() {
    const cartJson = await this.fetchCartJson();
    const existingCodes = (cartJson.discount_codes || [])
      .filter((d) => d.applicable !== false)
      .map((d) => d.code)
      .filter(Boolean);

    const promoLower = this.promoCode.toLowerCase();
    const codeAlreadyApplied = existingCodes.some((c) => c.toLowerCase() === promoLower);

    const newCodes = codeAlreadyApplied ? existingCodes : [...existingCodes, this.promoCode];

    const formData = new FormData();
    formData.append('discount', newCodes.join(','));
    formData.append('attributes[gift_promo_auto_applied]', 'true');

    const config = window.FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    const response = await fetch(window.FoxTheme.routes.cart_update_url, config);
    if (!response.ok) throw new Error('Failed to apply discount code');
    return response.json().catch(() => ({}));
  }

  /**
   * Remove the promo discount code only if WE applied it. Manual codes the
   * customer typed in are left alone.
   */
  async removeAutoAppliedCode() {
    const cartJson = await this.fetchCartJson();
    const promoLower = this.promoCode.toLowerCase();

    const remainingCodes = (cartJson.discount_codes || [])
      .map((d) => d.code)
      .filter((c) => c && c.toLowerCase() !== promoLower);

    const formData = new FormData();
    formData.append('discount', remainingCodes.join(','));
    formData.append('attributes[gift_promo_auto_applied]', '');

    const config = window.FoxTheme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    const response = await fetch(window.FoxTheme.routes.cart_update_url, config);
    if (!response.ok) throw new Error('Failed to remove auto-applied code');
    return response.json().catch(() => ({}));
  }

  fetchCartJson() {
    return fetch(`${window.FoxTheme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    }).then((response) => {
      if (!response.ok) throw new Error('Failed to read cart');
      return response.json();
    });
  }

  /**
   * Refresh the cart UI for both the cart drawer and the standalone cart
   * page. We do our own section bundling so it does not matter which
   * surface the user is on:
   *
   *   1. Ask any registered cart sections to identify themselves via the
   *      `cart:grouped-sections` event (cart-drawer + main-cart respond).
   *   2. Fetch each section's rendered HTML and the latest cart JSON in
   *      parallel.
   *   3. Publish `cartUpdate` with bundled sections so `CartItems` swaps in
   *      the new HTML for both the drawer and the cart page automatically.
   *   4. As a final belt-and-suspenders fall-through, also dispatch
   *      `cart:refresh` so any other custom listeners stay in sync.
   */
  async dispatchCartRefresh() {
    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const uniqueSections = [...new Set(sectionsToBundle.filter(Boolean))];

      const [cartJson, ...sectionResponses] = await Promise.all([
        this.fetchCartJson(),
        ...uniqueSections.map((sectionId) =>
          fetch(`${window.FoxTheme.routes.root_url || '/'}?section_id=${sectionId}`).then((r) =>
            r.ok ? r.text() : ''
          )
        ),
      ]);

      cartJson.sections = cartJson.sections || {};
      uniqueSections.forEach((sectionId, idx) => {
        cartJson.sections[sectionId] = sectionResponses[idx] || '';
      });

      if (window.FoxTheme?.pubsub) {
        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart: cartJson,
        });
      }
    } catch (error) {
      console.error('[gift-promo-selector] Failed to refresh cart UI:', error);
    } finally {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: false },
        })
      );
    }
  }

  get isActive() {
    return this.dataset.active === 'true';
  }

  get isPromoEligible() {
    return this.dataset.promoEligible === 'true';
  }

  get codeApplied() {
    return this.dataset.codeApplied === 'true';
  }

  get autoApplied() {
    return this.dataset.autoApplied === 'true';
  }

  get promoCode() {
    return this.dataset.promoCode || '';
  }

  get currentGiftLine() {
    const line = parseInt(this.dataset.currentGiftLine || '0', 10);
    return line > 0 ? line : null;
  }
}

if (!customElements.get('gift-promo-selector')) {
  customElements.define('gift-promo-selector', GiftPromoSelector);
}
